import { Send, Minimize2, Settings, BarChart3, Activity, AlertCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useToast } from "@/app/_module/hooks/use-toast";
import { useInsuraBot } from "@/app/_module/hooks/ai-hooks";
import type { AIContextType } from "@/app/_module/types/ai-types";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { getAdminAccess, getAIContextForUser } from "@/app/_module/utils/admin-access";
import chatbotService, { type ChatContextType } from "@/app/_module/services/chatbotService";
import { UnderwritingDocumentIngest } from "./UnderwritingDocumentIngest";
import { UnifiedInsuraBot } from "./UnifiedInsuraBot";
import { getCurrentAIConfig } from "../config/ai-config";
import { useTrialStatus } from "@/app/_module/hooks/useTrialStatus";


import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import { SheetContent, SheetHeader, SheetTitle, Sheet } from "./ui/sheet";
import { usePathname } from "next/navigation";
import { Badge } from "./ui/badge";
import { Alert, AlertDescription } from "./ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

interface InsuraBotProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enableAdminFeatures?: boolean;
  defaultContext?: AIContextType;
  useUnifiedAI?: boolean; // Optional prop to override unified AI system (defaults to config)
  contextMetadata?: Record<string, any>; // Additional metadata for context (e.g., available resources)
  // Pass false for secondary mounts (lead-details quote flow) so this
  // instance doesn't mirror its open state into the shared isInsuraBotOpen
  // flag — the layout's nav instance renders from that flag and would open
  // an empty bot on top. See UnifiedInsuraBot.
  syncOpenToContext?: boolean;
}

// Function to clean InsuraBot response formatting - removes special characters, preserves white space
const cleanInsuraBotResponse = (rawText: string): string => {
  if (!rawText) return '';
  
  const cleanText = rawText
    // Remove markdown formatting characters but preserve content structure
    .replace(/\*{3,}/g, '') // Remove triple+ asterisks
    .replace(/\*{2}([^*]+?)\*{2}/g, '$1') // Bold: **text** -> text
    .replace(/\*([^*\n]+?)\*/g, '$1') // Italic: *text* -> text
    .replace(/#{1,6}\s*/g, '') // Remove hash headers (### -> nothing)
    .replace(/`{1,3}([^`]+?)`{1,3}/g, '$1') // Remove code blocks
    .replace(/_{2,}/g, '') // Remove multiple underscores
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Links: [text](url) -> text
    
    // Remove other special characters but keep structure
    .replace(/~~([^~]+?)~~/g, '$1') // Strikethrough
    .replace(/^\s*[-*+]\s+/gm, '• ') // Convert list markers to bullets
    .replace(/^\s*\d+\.\s+/gm, '• ') // Convert numbered lists to bullets
    
    // Clean up extra whitespace but preserve paragraph breaks
    .replace(/\n\s*\n\s*\n/g, '\n\n') // Max 2 consecutive newlines
    .replace(/[ \t]+/g, ' ') // Multiple spaces to single space
    .trim();
    
  return cleanText;
};

export const InsuraBot = function ({
  open,
  onOpenChange,
  enableAdminFeatures = false,
  defaultContext = "general",
  useUnifiedAI, // Allow override, but default to config
  contextMetadata,
  syncOpenToContext
}: InsuraBotProps) {
  // Get current AI configuration
  const aiConfig = getCurrentAIConfig();

  // Use prop override if provided, otherwise use config
  const shouldUseUnifiedAI = useUnifiedAI !== undefined ? useUnifiedAI : aiConfig.useUnifiedAI;

  // If unified AI is enabled, render the new component
  if (shouldUseUnifiedAI) {
    return (
      <UnifiedInsuraBot
        contextMetadata={contextMetadata}
        defaultContext={defaultContext as 'general' | 'underwriting' | 'resources'}
        enableAdminFeatures={enableAdminFeatures}
        onOpenChange={onOpenChange}
        open={open}
        syncOpenToContext={syncOpenToContext}
      />
    );
  }

  // Otherwise, render the legacy component
  return <LegacyInsuraBot
    contextMetadata={contextMetadata}
    defaultContext={defaultContext}
    enableAdminFeatures={enableAdminFeatures}
    onOpenChange={onOpenChange}
    open={open}
  />;
};

// Legacy InsuraBot component (renamed from the original)
const LegacyInsuraBot = function ({ 
  open, 
  onOpenChange,
  enableAdminFeatures = false,
  defaultContext = "general" 
}: Omit<InsuraBotProps, 'useUnifiedAI'>) {
  // ⚠️ `.mobile-insurabot` existed in globals.css doing exactly the right thing
  // (safe-area insets, clears the header and the bottom tab bar) and was
  // applied to NOTHING — the same dead-CSS bug as `.mobile-dialer`. Without it
  // the w-96 right-side sheet covers the tab bar and runs under the notch.
  const pathname = usePathname();
  const isMobileShell = !!pathname?.startsWith("/m");
  const sheetClass = `p-0 flex flex-col [&>button]:hidden ${
    isMobileShell ? "mobile-insurabot" : "w-96"
  }`;

  const { toast } = useToast();
  const { user, isInitializing } = useUserContext();
  const { isViewerMode } = useTrialStatus();
  
  // Don't render if user context is not ready
  if (isInitializing || !user) {
    return null;
  }
  const [inputMessage, setInputMessage] = useState("");
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [legacyMode, setLegacyMode] = useState(false);
  const [showSampleQuestions, setShowSampleQuestions] = useState(true);
  
  // Sample questions based on context and user role
  const getSampleQuestions = () => {
    const currentCtx = !legacyMode ? enhancedBot.currentContext : "general";
    
    switch (currentCtx) {
      case "admin_analytics":
        // System admin - full platform analytics
        return [
          "Show me system-wide performance metrics",
          "What's the overall platform conversion rate?",
          "Analyze cross-agency success patterns",
          "Generate platform efficiency report",
          "Which agencies are top performers?"
        ];
      case "agency_admin":
        // Agency-level admin (agency owner or agency admin role)
        if (adminAccess.isSystemAdmin) {
          return [
            "Show me this agency's performance metrics",
            "What's this agency's conversion rate?", 
            "Analyze this agency's team patterns",
            "Generate agency-specific report",
            "How are this agency's agents performing?"
          ];
        } else {
          return [
            "Show me my agency's performance metrics",
            "What's our agency conversion rate?",
            "Analyze our team's call patterns",
            "Generate my agency efficiency report",
            "How are my agents performing?"
          ];
        }
      case "underwriting":
        return [
          "What are the age requirements for life insurance?",
          "What medical conditions affect eligibility?",
          "How is BMI evaluated for underwriting?",
          "What are the smoking requirements?",
          "What coverage amounts are available?",
          "How are premiums calculated?",
          "What documents are required for underwriting?",
          "How long does the underwriting process take?",
          "What are the different policy types?",
          "Can I get coverage with pre-existing conditions?",
          "What are the underwriting guidelines for term life?",
          "Help me assess this applicant's risk",
          "Explain medical exam requirements",
          "Review policy exclusions"
        ];
      case "carrier_mapping":
        return [
          "Which carrier is best for young adults?",
          "Compare rates for seniors",
          "Show carrier appointment requirements",
          "Explain commission structures"
        ];
      default:
        // Context-aware questions based on user type and agency role
        if (adminAccess.isSystemAdmin) {
          return [
            "How can I optimize platform-wide sales processes?",
            "What are the latest insurance industry trends?",
            "Help me analyze system performance",
            "Explain advanced administrative features"
          ];
        } else if (adminAccess.isAgencyOwner || adminAccess.isAgencyAdmin) {
          return [
            "How can I optimize my agency's sales process?",
            "What are the latest insurance industry trends?",
            "Help me analyze my team's performance",
            "Explain agency management best practices"
          ];
        } else if (user?.user?.userType === 'agent') {
          return [
            "How can I improve my sales process?",
            "What are the latest insurance trends?",
            "Help me follow up with leads",
            "Explain policy types and benefits"
          ];
        } else {
          // Fallback for trial, viewer, or any other user types
          return [
            "How can I get started with the platform?",
            "What features are available to me?",
            "Help me understand insurance basics",
            "How can I improve my access level?"
          ];
        }
    }
  };
  
  // Determine user role and admin capabilities using utility functions
  const agencyRole = user?.currentAgency?.role;
  const adminAccess = getAdminAccess(user?.user?.userType || 'agent', agencyRole);
  const shouldEnableAdminFeatures = enableAdminFeatures && (
    adminAccess.hasSystemAccess || adminAccess.hasAgencyManagement
  );
  
  // Enhanced AI hook for all admin types (system admin, agency owner, agency admin)
  const enhancedBot = useInsuraBot({
    enableAdminContext: shouldEnableAdminFeatures,
    contextType: getAIContextForUser(user?.user?.userType || 'agent', agencyRole) || defaultContext
  });
  
  // Legacy state for backward compatibility
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      text: "Hello! I'm InsuraBot, your AI assistant. How can I help you today?",
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [contextType] = useState<ChatContextType>("general");
  const [loading, setLoading] = useState(false);
  const [hipaaRedacted, setHipaaRedacted] = useState(false);
  const [retryIn, setRetryIn] = useState<number | null>(null);
  const lastOpenAtRef = useRef<number>(0);
  const SESSION_STORAGE_KEY = 'insura_bot_sessionId';

  // Determine which mode to use - enhanced for admins, legacy for others
  useEffect(() => {
    setLegacyMode(!shouldEnableAdminFeatures);
  }, [shouldEnableAdminFeatures]);

  // Enhanced mode - use AI hooks
  useEffect(() => {
    if (open && !legacyMode) {
      enhancedBot.setIsOpen(true);
    } else if (!open && !legacyMode) {
      enhancedBot.setIsOpen(false);
    }
  }, [open, legacyMode, enhancedBot]);

  // Legacy mode initialization
  const loadHistory = async (id: string) => {
    if (!id || id === 'undefined' || id === 'null') {

      return;
    }
    
    try {
      const history = await chatbotService.getHistory(id);
      const mapped: Message[] = history.map((m, idx) => ({
        id: m.id || `${idx}`,
        text: m.content,
        isUser: m.role === "user",
        timestamp: m.createdAt ? new Date(m.createdAt) : new Date(),
      }));
      // Keep greeting as first if present, then append history
      setMessages((prev) => {
        const hasGreeting = prev.length && !prev[0].isUser;
        const base = hasGreeting ? [prev[0]] : [];
        return [...base, ...mapped];
      });
    } catch (error: unknown) {
      // Handle 404 (session not found) and 400 (invalid session) by creating a new session
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 400) {
        try {
          const newSession = await chatbotService.createSession({ contextType });
          if (newSession && newSession.sessionId) {
            setSessionId(newSession.sessionId);
            // Store new session ID
            try {
              if (typeof window !== 'undefined') {
                localStorage.setItem(SESSION_STORAGE_KEY, newSession.sessionId);
              }
            } catch {}
            // Don't try to load history for new session - just start fresh
            return;
          }
        } catch (createError) {

        }
      }
      
      // For other errors, just log and continue without showing toast

    }
  };

  // Legacy mode session initialization with caching & debounce
  useEffect(() => {
    if (!open || !legacyMode) return;
    // simple debounce to avoid bursts on rapid toggles
    const now = Date.now();
    if (now - lastOpenAtRef.current < 500) {
      return;
    }
    lastOpenAtRef.current = now;

    const initializeChat = async () => {
      try {
        // Try cached session first
        let cachedId: string | null = null;
        try {
          cachedId = typeof window !== 'undefined' ? localStorage.getItem(SESSION_STORAGE_KEY) : null;
        } catch {}

        if (cachedId && cachedId !== 'undefined' && cachedId !== 'null') {
          setSessionId(cachedId);
          try {
            await loadHistory(cachedId);
            return; // reuse successful
          } catch {
            // fall through to create a new session
          }
        }

        // Create a new session if no valid cached session
        const session = await chatbotService.createSession({ contextType });
        
        if (!session || !session.sessionId || session.sessionId === 'undefined') {

          return;
        }
        
        setSessionId(session.sessionId);
        try {
          if (typeof window !== 'undefined') {
            localStorage.setItem(SESSION_STORAGE_KEY, session.sessionId);
          }
        } catch {}

        await loadHistory(session.sessionId);
      } catch (error: unknown) {
        // Handle 429 rate limit on session create with retry-after
        const anyErr = error as Record<string, unknown>;
        const extractRetry = () => {
          const direct = anyErr?.retryAfterSeconds;
          if (typeof direct === 'number') return direct;
          if (typeof direct === 'string') {
            const n = parseInt(direct, 10); if (!isNaN(n)) return n;
          }
          const response = anyErr?.response as { data?: Record<string, unknown>; headers?: Record<string, unknown> } | undefined;
          const bodyVal = response?.data?.retryAfterSeconds;
          if (typeof bodyVal === 'number') return bodyVal;
          if (typeof bodyVal === 'string') { const n = parseInt(bodyVal, 10); if (!isNaN(n)) return n; }
          const headerVal = response?.headers?.['retry-after'] as unknown;
          if (typeof headerVal === 'number') return headerVal;
          if (typeof headerVal === 'string') { const n = parseInt(headerVal, 10); if (!isNaN(n)) return n; }
          return undefined;
        };
        const status = (anyErr?.response as { status?: number } | undefined)?.status;
        if (status === 429) {
          const retry = extractRetry();
          if (typeof retry === 'number' && retry > 0) {
            setRetryIn(retry);
            setTimeout(() => {
              // retry once after backoff
              setRetryIn(null);
              void initializeChat();
            }, retry * 1000);
            return;
          }
        }
        const msg = (() => {
          if (error && typeof error === 'object' && 'message' in (error as object)) {
            const m = (error as { message?: unknown }).message;
            return typeof m === 'string' ? m : String(m);
          }
          return String(error);
        })();

        toast({ title: "Chat initialization failed", description: msg, variant: "destructive" });
      }
    };
    
    void initializeChat();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, legacyMode]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    // Hide sample questions once user sends a message
    setShowSampleQuestions(false);
    
    // Enhanced mode - use AI service hook
    if (!legacyMode && enhancedBot) {
      try {
        await enhancedBot.sendMessage(inputMessage.trim());
        setInputMessage("");
      } catch (error) {

        toast({
          title: "Failed to send message",
          description: error instanceof Error ? error.message : "Unknown error",
          variant: "destructive"
        });
      }
      return;
    }
    
    // Legacy mode - validate sessionId before sending
    if (!sessionId || sessionId === 'undefined' || sessionId === 'null') {
      toast({ 
        title: "No active chat session", 
        description: "Please close and reopen the assistant to start a new session.", 
        variant: "destructive" 
      });
      return;
    }

    const text = inputMessage.trim().slice(0, 10000);
    const userMessage: Message = {
      id: Date.now().toString(),
      text,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");

    setLoading(true);
    try {
      const res = await chatbotService.sendMessage({
        message: text,
        sessionId,
        contextType,
      });
      if (res.redacted) setHipaaRedacted(true);
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: (res.response ?? res.message ?? ""),
        isUser: false,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMessage]);
    } catch (error: unknown) {
      // Handle 400 invalid sessionId by recreating session and retrying once
      try {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 400) {
          const newSession = await chatbotService.createSession({ contextType });
          setSessionId(newSession.sessionId);
          try {
            if (typeof window !== 'undefined') {
              localStorage.setItem(SESSION_STORAGE_KEY, newSession.sessionId);
            }
          } catch {}
          const res = await chatbotService.sendMessage({
            message: text,
            sessionId: newSession.sessionId,
            contextType,
          });
          if (res.redacted) setHipaaRedacted(true);
          const botMessage: Message = {
            id: (Date.now() + 1).toString(),
            text: (res.response ?? res.message ?? ""),
            isUser: false,
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, botMessage]);
          return;
        }
      } catch {}
      const msg = (() => {
        if (error && typeof error === 'object' && 'message' in (error as object)) {
          const m = (error as { message?: unknown }).message;
          return typeof m === 'string' ? m : String(m);
        }
        return String(error);
      })();
      if (/429/.test(msg)) {
        // Try to read retryAfterSeconds if backend included it in body/headers
        const retry = (() => {
          if (error && typeof error === 'object') {
            const anyErr = error as Record<string, unknown>;
            const direct = anyErr.retryAfterSeconds;
            if (typeof direct === 'number') return direct;
            if (typeof direct === 'string') {
              const n = parseInt(direct, 10);
              if (!isNaN(n)) return n;
            }
            const response = anyErr.response as { data?: Record<string, unknown>; headers?: Record<string, unknown> } | undefined;
            const bodyVal = response?.data?.retryAfterSeconds;
            if (typeof bodyVal === 'number') return bodyVal;
            if (typeof bodyVal === 'string') {
              const n = parseInt(bodyVal, 10);
              if (!isNaN(n)) return n;
            }
            const headerVal = response?.headers?.['retry-after'] as unknown;
            if (typeof headerVal === 'number') return headerVal;
            if (typeof headerVal === 'string') {
              const n = parseInt(headerVal, 10);
              if (!isNaN(n)) return n;
            }
          }
          return undefined;
        })();
        if (typeof retry === 'number' && retry > 0) {
          setRetryIn(retry);
          toast({ title: "Too many requests", description: `Please wait ~${retry}s and retry`, variant: "destructive" });
        } else {
          toast({ title: "Too many requests", description: "Please wait and retry", variant: "destructive" });
        }
      } else if (/413|payload too large/i.test(msg)) {
        toast({ title: "Message too long", description: "Try under ~5k chars; DTO limit is 10k", variant: "destructive" });
      } else if (/400/.test(msg)) {
        toast({ title: "Blocked by guard", description: "Content blocked (DLP/guard)", variant: "destructive" });
      } else {
        toast({ title: "Send failed", description: msg, variant: "destructive" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSendMessage();
    }
  };

  return (
    <Sheet onOpenChange={onOpenChange} open={open}>
      <SheetContent
        aria-describedby="fexi-bot-description"
        className={sheetClass}
        side="right"
      >
        {" "}
        <SheetHeader className="p-4 border-b bg-blue-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg viewBox="0 0 500 410" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600 overflow-visible">
                <path d="M386.363 22.731H113.636C88.5321 22.731 68.1814 43.0816 68.1814 68.1855V250.004C68.1814 275.108 88.5321 295.458 113.636 295.458H386.363C411.467 295.458 431.818 275.108 431.818 250.004V68.1855C431.818 43.0816 411.467 22.731 386.363 22.731Z" stroke="currentColor" strokeWidth="45.4545" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22.7263 386.371H477.272" stroke="currentColor" strokeWidth="45.4545" strokeLinecap="round" strokeLinejoin="round"/>
                <path className="insurabot-eye" d="M204.546 113.636V159.091" stroke="currentColor" strokeWidth="45.4545" strokeLinecap="round" strokeLinejoin="round"/>
                <path className="insurabot-eye insurabot-eye-r" d="M295.454 113.636V159.091" stroke="currentColor" strokeWidth="45.4545" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <SheetTitle className="text-blue-900">InsuraBot</SheetTitle>
              {!legacyMode && (
                <Badge variant="secondary" className="text-xs">
                  Enhanced
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {shouldEnableAdminFeatures && (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowAnalytics(!showAnalytics)}
                    className="h-8 w-8 p-0"
                  >
                    <BarChart3 className="h-4 w-4" />
                  </Button>
                  <Select
                    value={enhancedBot.currentContext}
                    onValueChange={(value: AIContextType) => enhancedBot.switchContext(value)}
                  >
                    <SelectTrigger className="w-[120px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="admin_analytics">Analytics</SelectItem>
                      <SelectItem value="underwriting">Underwriting</SelectItem>
                      <SelectItem value="carrier_mapping">Carriers</SelectItem>
                    </SelectContent>
                  </Select>
                  {enhancedBot.currentContext === 'underwriting' && (
                    <UnderwritingDocumentIngest
                      onDocumentIngested={(result) => {
                        toast({
                          title: "Document Ready",
                          description: `Underwriting guidelines for ${result.carrier_slug} are now available for questions.`,
                          variant: "default"
                        });
                      }}
                    />
                  )}
                </>
              )}
              {/* Sample Questions Toggle */}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowSampleQuestions(!showSampleQuestions)}
                className="h-8 w-8 p-0"
                title="Toggle sample questions"
              >
                <span className="text-xs">💡</span>
              </Button>
              <Button
                className="h-8 w-8"
                onClick={() => onOpenChange(false)}
                size="icon"
                variant="ghost"
              >
                <Minimize2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </SheetHeader>
        {!!hipaaRedacted && (
          <div className="mx-4 mt-3 rounded-md bg-amber-50 border border-amber-200 p-2 text-amber-900 text-xs">
            History is de-identified; content may appear redacted
          </div>
        )}
        {showAnalytics && shouldEnableAdminFeatures && enhancedBot.adminInsights && (
          <div className="mx-4 mt-3 rounded-md bg-green-50 border border-green-200 p-3">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="h-4 w-4 text-green-600" />
              <span className="text-sm font-semibold text-green-900">Analytics Summary</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-green-700">Call Success:</span>
                <span className="ml-1 font-medium">{enhancedBot.adminInsights.callAnalysis.successRate.toFixed(1)}%</span>
              </div>
              <div>
                <span className="text-green-700">Conversion:</span>
                <span className="ml-1 font-medium">{enhancedBot.adminInsights.leadInsights.conversionRate.toFixed(1)}%</span>
              </div>
              <div>
                <span className="text-green-700">Performance:</span>
                <span className="ml-1 font-medium">{enhancedBot.adminInsights.agencyMetrics.performance.toFixed(1)}%</span>
              </div>
              <div>
                <span className="text-green-700">Efficiency:</span>
                <span className="ml-1 font-medium">{enhancedBot.adminInsights.agencyMetrics.efficiency.toFixed(1)}%</span>
              </div>
            </div>
          </div>
        )}
        <div id="fexi-bot-description" className="sr-only">
          Ask questions and view chat history for the current session.
        </div>
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-4">
            {/* Sample Questions - Show when no messages (except greeting) */}
            {showSampleQuestions && 
             !isViewerMode &&
             ((!legacyMode && enhancedBot.messages.length <= 1) || 
              (legacyMode && messages.length <= 1)) && (
              <div className="space-y-3">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  💡 Try asking me about:
                </div>
                <div className="grid gap-2">
                  {getSampleQuestions().map((question, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      className="text-left h-auto p-3 text-xs leading-relaxed bg-blue-50 border-blue-200 hover:bg-blue-100 text-blue-700"
                      onClick={() => {
                        setInputMessage(question);
                        setShowSampleQuestions(false);
                      }}
                    >
                      {question}
                    </Button>
                  ))}
                </div>
                <div className="border-t pt-3 mt-3">
                  <div className="text-xs text-gray-500">Or type your own question below 👇</div>
                </div>
              </div>
            )}
            
            {/* Hide sample questions after first user message */}
            {/* Enhanced mode - use AI messages */}
            {!legacyMode && enhancedBot.messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.role === "user"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-900"
                  }`}
                >
                  <p className="text-sm">{message.content}</p>
                  
                  {message.role === "assistant" && message.metadata?.underwriting ? (
                    <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-blue-900">AI Underwriting Response</span>
                        <Badge variant="secondary" className="text-xs">
                          {(message.metadata.underwriting as any)?.carrier || 'Unknown'}
                        </Badge>
                      </div>
                      <div className="space-y-1 text-blue-700">
                        <div className="flex justify-between">
                          <span>Model:</span>
                          <span className="font-medium">{(message.metadata.usage as any)?.model || 'Unknown'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Confidence:</span>
                          <span className="font-medium">
                            {(((message.metadata.usage as any)?.confidence || 0) * 100).toFixed(1)}%
                          </span>
                        </div>
                        {(message.metadata.underwriting as any)?.evidence && (message.metadata.underwriting as any).evidence.length > 0 && (
                          <div>
                            <span className="font-medium">Evidence Sources:</span>
                            <div className="mt-1 space-y-1">
                              {(message.metadata.underwriting as any).evidence.slice(0, 2).map((source: any, idx: number) => (
                                <div key={idx} className="text-xs opacity-75">
                                  • {source.doc_key} ({source.relevance_score ? (source.relevance_score * 100).toFixed(0) : 'N/A'}% relevance)
                                </div>
                              ))}
                              {(message.metadata.underwriting as any).evidence.length > 2 && (
                                <div className="text-xs opacity-75">
                                  +{(message.metadata.underwriting as any).evidence.length - 2} more sources
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                  
                  <p
                    className={`text-xs mt-1 ${
                      message.role === "user" ? "text-blue-100" : "text-gray-500"
                    }`}
                  >
                    {message.timestamp.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              </div>
            ))}
            
            {/* Viewer Mode Block Message */}
            {isViewerMode && (
              <Alert className="bg-yellow-50 border-yellow-200">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-sm text-yellow-800">
                  <p className="font-medium">Chat is unavailable</p>
                  <p className="mt-1">
                    Your trial has expired or your subscription is inactive. Please upgrade to continue using InsuraBot.
                  </p>
                </AlertDescription>
              </Alert>
            )}
            
            {/* Legacy mode - use legacy messages */}
            {legacyMode && messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.isUser ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.isUser
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-900"
                  }`}
                >
                  <p className="text-sm">{message.isUser ? message.text : cleanInsuraBotResponse(message.text)}</p>
                  <p
                    className={`text-xs mt-1 ${
                      message.isUser ? "text-blue-100" : "text-gray-500"
                    }`}
                  >
                    {message.timestamp.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              </div>
            ))}
            
            {/* Viewer Mode Block Message */}
            {isViewerMode && (
              <Alert className="bg-yellow-50 border-yellow-200">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-sm text-yellow-800">
                  <p className="font-medium">Chat is unavailable</p>
                  <p className="mt-1">
                    Your trial has expired or your subscription is inactive. Please upgrade to continue using InsuraBot.
                  </p>
                </AlertDescription>
              </Alert>
            )}
          </div>
        </ScrollArea>
        <div className="p-4 border-t bg-white">
          <div className="flex gap-2">
            <Input
              className="flex-1"
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={isViewerMode ? "Chat is unavailable. Please upgrade to continue using InsuraBot." : "Type your message..."}
              value={inputMessage}
              disabled={isViewerMode || ((!legacyMode && enhancedBot.isLoading) || (legacyMode && loading))}
            />
            <Button 
              disabled={isViewerMode || ((legacyMode && (loading || (retryIn !== null && retryIn > 0))) || (!legacyMode && enhancedBot.isLoading))} 
              onClick={handleSendMessage} 
              size="icon"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          {retryIn !== null && retryIn > 0 && (
            <div className="text-xs text-amber-700 mt-2">Rate limited. Try again in ~{retryIn}s.</div>
          )}
          {!legacyMode && shouldEnableAdminFeatures && (
            <div className="text-xs text-blue-600 mt-2 flex items-center gap-2">
              <span>Context: {enhancedBot.currentContext}</span>
              {enhancedBot.isAnalyticsLoading && (
                <span className="text-gray-500">• Loading analytics...</span>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
